import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
} from '@mui/material';
import apiCall from '../utils/apiCall';

const PAGE_SIZE = 20; // Number of logs to fetch per page

function LogViewer() {
    const [logs, setLogs] = useState([]);
    const [levelFilter, setLevelFilter] = useState('');
    const [routeIncludesFilter, setRouteIncludesFilter] = useState('');
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const logsContainerRef = useRef(null);

    const fetchLogs = useCallback(async () => {
        if (loading || !hasMore) return;
        setLoading(true);

        try {
            // const response = await axios.get('/logs', {
            //     params: {
            //         level: levelFilter,
            //         routeIncludes: routeIncludesFilter,
            //         page: page,
            //         pageSize: PAGE_SIZE,
            //     },
            // });
            const response=await apiCall(`/admin/logs?${new URLSearchParams({
                level: levelFilter,
                routeIncludes: routeIncludesFilter,
                page: page,
                pageSize: PAGE_SIZE,
            })}`, 'GET', null);

            if (response.logs.length > 0) {
                setLogs((prevLogs) => [...prevLogs, ...response.logs]);
                setPage((prevPage) => prevPage + 1);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [levelFilter, routeIncludesFilter, page, hasMore, loading]);

    useEffect(() => {
        fetchLogs(); // Initial load
    }, [fetchLogs]);

    const handleScroll = useCallback(() => {
        if (logsContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 20 && !loading && hasMore) {
                fetchLogs();
            }
        }
    }, [fetchLogs, loading, hasMore]);

    useEffect(() => {
        const container = logsContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    const handleLevelChange = (event) => {
        setLevelFilter(event.target.value);
        setLogs([]); // Reset logs on filter change
        setPage(0);
        setHasMore(true);
    };

    const handleRouteIncludesChange = (event) => {
        setRouteIncludesFilter(event.target.value);
        setLogs([]); // Reset logs on filter change
        setPage(0);
        setHasMore(true);
    };

    const handleClearFilters = () => {
        setLevelFilter('');
        setRouteIncludesFilter('');
        setLogs([]); // Reset logs on filter clear
        setPage(0);
        setHasMore(true);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                API Logs
            </Typography>

            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl>
                    <InputLabel id="level-select-label">Filter by Level</InputLabel>
                    <Select
                        labelId="level-select-label"
                        id="level-select"
                        value={levelFilter}
                        label="Filter by Level"
                        onChange={handleLevelChange}
                    >
                        <MenuItem value="">All Levels</MenuItem>
                        <MenuItem value="error">Error</MenuItem>
                        <MenuItem value="warn">Warn</MenuItem>
                        <MenuItem value="info">Info</MenuItem>
                        <MenuItem value="debug">Debug</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    label="Filter by Route (includes)"
                    value={routeIncludesFilter}
                    onChange={handleRouteIncludesChange}
                />

                <Button onClick={handleClearFilters} variant="outlined">
                    Clear Filters
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'auto', maxHeight: '100vh' }} ref={logsContainerRef}>
                <TableContainer>
                    <Table sx={{ minWidth: 700 }} aria-label="log table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Timestamp</TableCell>
                                <TableCell>Level</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell>Metadata</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow
                                    key={log._id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell component="th" scope="row">
                                        {new Date(log.timestamp).toLocaleString()}
                                    </TableCell>
                                    <TableCell>{log.level}</TableCell>
                                    <TableCell>
                                        <pre>{JSON.stringify(log.message, null, 2)}</pre>
                                    </TableCell>
                                    <TableCell>
                                        <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            )}
                            {!hasMore && logs.length > 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        <Typography variant="caption">No more logs to load.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}

export default LogViewer;